import { useEffect } from 'react';
import { useSignageConfig } from '@/config/ConfigProvider';
import { useSignageStore } from '@/store/signageStore';
import { FlatLayout } from '@/widgets/FlatLayout';
import { SliderLayout } from '@/widgets/SliderLayout';

/**
 * The config-driven renderer. Port of `moduleLoader.vue`.
 *
 * Reads `components` and `layout` from `useSignageConfig()` rather than from props: the
 * legacy prop drilling through `Home.vue` existed because Vue's `globalProperties` had to be
 * threaded down by hand, and React context is the direct equivalent of what that was working
 * around.
 */
export function ModuleLoader() {
  const config = useSignageConfig();

  /**
   * `moduleLoaded` — one third of the MQTT connect gate (docs/MIGRATION.md §5):
   * `preventerLoaded && moduleLoaded && topicItems.size > 0`.
   *
   * Empty deps, so it fires on mount and never again. Two things about the timing that look
   * like races and are not:
   *
   * 1. **It runs after the widgets have registered their topics.** React runs child effects
   *    before parent effects, and every widget's `useWidgetData` registration is a child
   *    effect of this one. So by the time this line executes, `topicItems` is already
   *    populated — no `setTimeout`, no `requestAnimationFrame`, no ordering hack.
   * 2. **StrictMode double-invokes it in dev.** `setModuleLoaded()` is an idempotent
   *    `set({ moduleLoaded: true })` — there is no toggle and no counter — so the second
   *    call writes the same value. Asserted in `ModuleLoader.test.tsx`, because SYM-489's
   *    connect gate hangs off this flag and a double-fire that flipped state twice would be
   *    a genuine bug.
   *
   * Read imperatively via `getState()`: subscribing to the action would re-render the whole
   * widget tree for nothing.
   */
  useEffect(() => {
    useSignageStore.getState().setModuleLoaded();
  }, []);

  /**
   * The layout switch (SYM-492). Presence of a top-level `slider` block is the whole
   * condition, exactly as legacy `haveSlider` was.
   *
   * None of the seven deployed configs carries one, so every live display takes the flat
   * path today. `szeged_2`, `szeged_3`, `szeged_4`, `corio`, `freshwaves`, `hbr_tcc_2` and
   * `hbr_tcc_3` do carry `slide: n` on some components, but legacy `moduleLoader.vue` only
   * reads `component.slide` inside `if (slider)` — with no `slider` block those values are
   * inert and every component renders flat. That is the behaviour reproduced here, and it is
   * why the branch is on `config.slider` alone and never on whether components carry a
   * `slide`.
   *
   * Safe below the effect above: it is an early return, not a conditional hook.
   */
  if (config.slider) {
    return (
      <SliderLayout
        slider={config.slider}
        components={config.components}
        spacing={config.layout.components}
      />
    );
  }

  return <FlatLayout components={config.components} spacing={config.layout.components} />;
}
